import { configureDragAndDrop } from './drag_and_drop'
import {
  getPendingAttachmentHtml,
  onFilesRejected,
  renderPendingAlert,
} from './attachments'

export interface IAttachmentConfig {
  renderNoAttachmentPath: string
  pendingAttachmentPath: string
}

interface IExistingAttachmentConfig {
  attachmentId: string
  fileName: string
  fileSize: number
  fileCategory: string
  attachmentType: string
}

const extractFileName = (fileName: string) => {
  // eslint-disable-next-line no-control-regex
  const sanitized = fileName.replace(/[^\x00-\x7F]/g, '')
  return sanitized.replace(/\./g, '_')
}

const createHiddenFields = (
  fileName: string,
  attachmentType = 'application',
  existingAttachment = false,
) => {
  const form = document.getElementById('inbox-compose-form')
  const hiddenFileField = document.createElement('input')
  if (!existingAttachment) {
    hiddenFileField.setAttribute('type', 'file')
    hiddenFileField.setAttribute('id', `id_${fileName}`)
    hiddenFileField.setAttribute('name', `id_${fileName}`)
    hiddenFileField.style.display = 'none'

    const hiddenCategoryField = document.createElement('input')
    hiddenCategoryField.setAttribute('type', 'hidden')
    hiddenCategoryField.setAttribute('id', `id_${fileName}_category`)
    hiddenCategoryField.setAttribute('name', `id_${fileName}_category`)
    if (form) {
      form.insertBefore(hiddenCategoryField, form.lastChild)
    }
  } else {
    hiddenFileField.setAttribute('type', 'hidden')
    hiddenFileField.setAttribute(
      'id',
      `id_${fileName}_existing_${attachmentType}`,
    )
    hiddenFileField.setAttribute(
      'name',
      `id_${fileName}_existing_${attachmentType}`,
    )
  }
  if (form) {
    form.insertBefore(hiddenFileField, form.lastChild)
  }
}

const removeHiddenFields = (
  fileName: string,
  attachmentType = 'application',
) => {
  const hiddenCategoryField = document.getElementById(`id_${fileName}_category`)
  const hiddenAttachmentField = document.getElementById(`id_${fileName}`)
  const hiddenExistingAttachmentField = document.getElementById(
    `id_${fileName}_existing_${attachmentType}`,
  )
  if (hiddenAttachmentField && hiddenCategoryField) {
    hiddenAttachmentField.remove()
    hiddenCategoryField.remove()
  }
  if (hiddenExistingAttachmentField) {
    hiddenExistingAttachmentField.remove()
  }
}

const populateHiddenCategoryField = (fileName: string, category: string) => {
  const hiddenCategoryField = document.getElementById(
    `id_${fileName}_category`,
  ) as HTMLInputElement
  if (hiddenCategoryField) {
    hiddenCategoryField.value = category
  }
}

const populateHiddenFileField = (
  file?: File,
  existingAttachment?: IExistingAttachmentConfig,
) => {
  if (file) {
    const hiddenFileField = document.getElementById(
      `id_${extractFileName(file.name)}`,
    ) as HTMLInputElement
    // We need to create another DataTransfer instance to assign file to input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    if (hiddenFileField) {
      hiddenFileField.files = dataTransfer.files
    }
  }
  if (existingAttachment) {
    const hiddenFileField = document.getElementById(
      `id_${extractFileName(existingAttachment.fileName)}_existing_${existingAttachment.attachmentType}`,
    ) as HTMLInputElement
    if (hiddenFileField) {
      hiddenFileField.value = existingAttachment.attachmentId
    }
  }
}

export const getPendingAttachmentCount = () => {
  const categories = Array.from(
    document.getElementsByClassName('category-select'),
  )
  return categories.filter((el) => !el.classList.contains('selected')).length
}

const configureButtonRemove = (
  button: Element,
  fileName: string,
  attachmentType?: string,
) => {
  button?.addEventListener('click', () => {
    const pendingAttachmentTr = button.parentElement?.closest('tr')
    if (pendingAttachmentTr) {
      const tbody = pendingAttachmentTr.parentElement?.closest('tbody')
      if (tbody && tbody.children.length <= 1) {
        const table = document.querySelector(
          '.hyper-email-attach',
        ) as HTMLTableRowElement
        if (table) {
          table.hidden = true
        }
      }
      pendingAttachmentTr.remove()
      removeHiddenFields(fileName, attachmentType)
      renderPendingAlert(getPendingAttachmentCount())
    }
  })
}

const configureSelectCategory = (select: HTMLSelectElement, file: File) => {
  select.addEventListener('change', () => {
    select.classList.add('selected')
    renderPendingAlert(getPendingAttachmentCount())
    const pendingAttachmentTr = select.parentElement?.closest(
      'tr',
    ) as HTMLTableRowElement
    pendingAttachmentTr.cells[0].replaceWith(document.createElement('td'))
    populateHiddenCategoryField(extractFileName(file.name), select.value)
  })
}

const configurePendingAttachmentTr = (
  pendingAttachmentTr: HTMLTableRowElement,
  file: File,
) => {
  const selectCategory = pendingAttachmentTr.querySelector('select')
  if (selectCategory) {
    configureSelectCategory(selectCategory, file)
  }

  const buttonRemove = pendingAttachmentTr.querySelector('button')
  if (buttonRemove) {
    configureButtonRemove(buttonRemove, extractFileName(file.name))
  }
}

const configureExistingAttachmentTr = (
  existingAttachmentTr: HTMLTableRowElement,
  existingAttachment: IExistingAttachmentConfig,
) => {
  createHiddenFields(
    extractFileName(existingAttachment.fileName),
    existingAttachment.attachmentType,
    true,
  )
  populateHiddenFileField(undefined, existingAttachment)
  existingAttachmentTr.cells[0].replaceWith(document.createElement('td'))
  const buttonRemove = existingAttachmentTr.querySelector('button')
  if (buttonRemove) {
    configureButtonRemove(
      buttonRemove,
      extractFileName(existingAttachment.fileName),
      existingAttachment.attachmentType,
    )
  }
}

export const renderPendingAttachment = async (
  config: IAttachmentConfig,
  file?: File,
  existingAttachment?: IExistingAttachmentConfig,
) => {
  const fileName = existingAttachment
    ? existingAttachment.fileName
    : file
      ? file.name
      : ''
  const fileSize = existingAttachment
    ? existingAttachment.fileSize
    : file
      ? file.size
      : 0
  const pendingAttachmentHtml = await getPendingAttachmentHtml(
    fileName,
    fileSize,
    config.pendingAttachmentPath,
  )

  if (pendingAttachmentHtml) {
    const pendingAttachmentTr = document.createElement('tr')
    pendingAttachmentTr.innerHTML = pendingAttachmentHtml
    pendingAttachmentTr.id = <string>existingAttachment?.attachmentId
    const table = document.querySelector(
      '.hyper-email-attach',
    ) as HTMLTableRowElement
    if (table) {
      table.hidden = false
    }
    const tbody = table?.querySelector('tbody')
    if (tbody) {
      tbody.insertBefore(pendingAttachmentTr, tbody.firstChild)
      // remove "no attachment" <tr>, if one exists.
      tbody.querySelector('tr>td[colspan="100%"]')?.parentElement?.remove()
    }
    if (file) {
      createHiddenFields(extractFileName(file.name), undefined, false)
      populateHiddenFileField(file)
      configurePendingAttachmentTr(pendingAttachmentTr, file)
    }
    if (existingAttachment) {
      configureExistingAttachmentTr(pendingAttachmentTr, existingAttachment)
    }
  }
}

export const configureEmailAttachments = (
  config: IAttachmentConfig,
  onFilesAccepted: (files: File[], config: IAttachmentConfig) => void,
) => {
  const dnd = document.getElementById('drag-and-drop')
  if (dnd) {
    configureDragAndDrop(dnd, onFilesAccepted, onFilesRejected, config)
  }
}
